import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import { formatPrice } from '../../utils/priceFormatter';
import { downloadQuotationPDF } from '../../utils/pdfGenerator';
import { showSuccess, showError, showLoading, hideLoading } from '../../utils/toast';
import BottomNav from '../../components/layouts/BottomNav';
import { FaArrowLeft, FaEye, FaDownload, FaFileInvoice } from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_BASE_URL || "http://localhost:5000/";

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch (error) {
    console.warn('Invalid cached user data. Clearing stale user storage.');
    localStorage.removeItem('user');
    return {};
  }
};

const MyQuotationsPage = () => {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchUserQuotations();
  }, []);

  const fetchUserQuotations = async () => {
    try {
      setIsLoading(true);
      let user = getStoredUser();

      // Recover profile from token if user cache is missing (common after PWA reloads/download flows).
      if (!user.id && localStorage.getItem('token')) {
        try {
          const meRes = await api.get('/users/me');
          user = meRes.data || {};
          if (user.id) {
            localStorage.setItem('user', JSON.stringify(user));
          }
        } catch (meError) {
          console.error('Failed to recover user profile from token:', meError);
        }
      }

      if (!user.id) {
        // Guest user - don't show error, just empty list or could show a dedicated component
        setQuotations([]);
        setIsLoading(false);
        return;
      }

      const response = await api.get(`/quotations/user/${user.id}`);
      setQuotations(response.data);
    } catch (error) {
      console.error('Error fetching quotations:', error);
      // Don't show error toast if it's just a 401/403 or auth issue, let the UI handle it
      if (error.response && error.response.status !== 401 && error.response.status !== 403) {
        showError('Failed to fetch quotations');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewQuotation = (quotation) => {
    setSelectedQuotation(quotation);
    setShowModal(true);
  };

  const handleDownloadQuotation = async (quotation) => {
    const tokenSnapshot = localStorage.getItem('token');
    const userSnapshot = localStorage.getItem('user');
    const ownerSnapshot = localStorage.getItem('session_owner');

    try {
      showLoading('Generating quotation...');

      // Record the download
      await api.put(`/quotations/${quotation.id}/download`);

      // Generate and download/share the PDF quotation
      await downloadQuotationPDF(quotation, quotation.items || []);

      // Restore session if browser/PWA download flow unexpectedly clears storage.
      if (tokenSnapshot && !localStorage.getItem('token')) {
        localStorage.setItem('token', tokenSnapshot);
      }
      if (userSnapshot && !localStorage.getItem('user')) {
        localStorage.setItem('user', userSnapshot);
      }
      if (ownerSnapshot && !localStorage.getItem('session_owner')) {
        localStorage.setItem('session_owner', ownerSnapshot);
      }

      hideLoading();
      showSuccess('Quotation downloaded successfully!');
    } catch (error) {
      console.error('Error downloading quotation:', error);
      hideLoading();
      showError('Failed to download quotation. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return '#6c757d';
      case 'sent': return '#007bff';
      case 'viewed': return '#17a2b8';
      case 'accepted': return '#28a745';
      case 'rejected': return '#dc3545';
      case 'expired': return '#ffc107';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'sent': return 'Sent';
      case 'viewed': return 'Viewed';
      case 'accepted': return 'Accepted';
      case 'rejected': return 'Rejected';
      case 'expired': return 'Expired';
      default: return 'Unknown';
    }
  };

  const getFirstImageUrl = (item) => {
    let imageUrl = "";
    if (item.product_image) {
      imageUrl = item.product_image;
      if (!imageUrl.startsWith("http")) {
        imageUrl = API_BASE + imageUrl.replace(/^\/+/, "");
      }
    }
    return imageUrl;
  };

  if (isLoading) {
    return (
      <div style={{
        background: "#fff",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: 40,
            height: 40,
            border: "3px solid #f3f3f3",
            borderTop: "3px solid #333",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 16px"
          }}></div>
          <div style={{ color: "#666" }}>Loading quotations...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#fff", minHeight: "100vh", paddingBottom: 74 }}>
      {/* Header */}
      <div style={{
        padding: "18px 10px 0 10px",
        background: "#fff",
        position: "sticky",
        top: 0,
        zIndex: 9
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 6px 10px 6px", marginBottom: 8
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
            <button
              onClick={() => navigate('/account')}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                marginRight: 10,
                display: "flex",
                alignItems: "center",
                cursor: "pointer"
              }}
              aria-label="Back"
            >
              <FaArrowLeft size={20} color="#222" />
            </button>
            <div style={{
              fontWeight: 700,
              fontSize: 21,
              color: "#232323",
              letterSpacing: "0.2px",
              marginTop: 2
            }}>
              My Quotations
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: "0 16px" }}>
        {quotations.length === 0 ? (
          <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 20px",
            textAlign: "center"
          }}>
            <div style={{
              fontSize: 48,
              marginBottom: 16,
              opacity: 0.3
            }}>
              <FaFileInvoice />
            </div>
            {/* Check if user is logged in to show appropriate message */}
            {!localStorage.getItem('token') ? (
              <>
                <div style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: "#333",
                  marginBottom: 8
                }}>
                  Login Required
                </div>
                <div style={{
                  fontSize: 14,
                  color: "#666",
                  marginBottom: 24
                }}>
                  Please login to view your quotation history
                </div>
                <button
                  onClick={() => navigate('/login')}
                  style={{
                    background: "#000",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "12px 24px",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer"
                  }}
                >
                  Login Now
                </button>
              </>
            ) : (
              <>
                <div style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: "#333",
                  marginBottom: 8
                }}>
                  No quotations found
                </div>
                <div style={{
                  fontSize: 14,
                  color: "#666",
                  marginBottom: 24
                }}>
                  You haven't created any quotations yet
                </div>
                <button
                  onClick={() => navigate('/products')}
                  style={{
                    background: "#333",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "12px 24px",
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer"
                  }}
                >
                  Browse Products
                </button>
              </>
            )}
          </div>
        ) : (
          <div>
            <div style={{
              fontSize: 16,
              fontWeight: 600,
              color: "#333",
              marginBottom: 16
            }}>
              Your Quotations ({quotations.length})
            </div>

            {quotations.map((quotation) => (
              <div key={quotation.id} style={{
                background: "#fff",
                borderRadius: 12,
                padding: 16,
                marginBottom: 12,
                border: "1px solid #e9ecef",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
              }}>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: 12
                }}>
                  <div>
                    <div style={{
                      fontWeight: 600,
                      fontSize: 16,
                      color: "#333",
                      marginBottom: 4
                    }}>
                      {quotation.quotation_number}
                    </div>
                    <div style={{
                      fontSize: 12,
                      color: "#666",
                      marginBottom: 4
                    }}>
                      Created: {new Date(quotation.created_at).toLocaleDateString()}
                    </div>
                    <div style={{
                      fontSize: 12,
                      color: "#666"
                    }}>
                      Valid until: {new Date(quotation.valid_until).toLocaleDateString()}
                    </div>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <div style={{
                      background: getStatusColor(quotation.status),
                      color: "#fff",
                      padding: "4px 8px",
                      borderRadius: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      marginBottom: 8
                    }}>
                      {getStatusText(quotation.status)}
                    </div>
                    <div style={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: "#333"
                    }}>
                      {formatPrice(quotation.total_amount)}
                    </div>
                  </div>
                </div>

                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                  <div style={{
                    fontSize: 12,
                    color: "#666"
                  }}>
                    {quotation.items?.length || 0} items
                  </div>

                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => handleViewQuotation(quotation)}
                      style={{
                        background: "#007bff",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 12px",
                        fontSize: 12,
                        fontWeight: 500,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4
                      }}
                    >
                      <FaEye size={10} />
                      View
                    </button>
                    <button
                      onClick={() => handleDownloadQuotation(quotation)}
                      style={{
                        background: "#28a745",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        padding: "6px 12px",
                        fontSize: 12,
                        fontWeight: 500,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4
                      }}
                    >
                      <FaDownload size={10} />
                      PDF
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quotation Details Modal */}
      {showModal && selectedQuotation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                Quotation Details - {selectedQuotation.quotation_number}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              {/* Customer Details */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                  Customer Information
                </h3>
                <div style={{
                  background: '#f8f9fa',
                  padding: '16px',
                  borderRadius: '8px',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px'
                }}>
                  <div>
                    <strong>Name:</strong> {selectedQuotation.customer_name}
                  </div>
                  <div>
                    <strong>Email:</strong> {selectedQuotation.customer_email}
                  </div>
                  <div>
                    <strong>Phone:</strong> {selectedQuotation.customer_phone}
                  </div>
                  <div>
                    <strong>Status:</strong>
                    <span style={{
                      background: getStatusColor(selectedQuotation.status),
                      color: "#fff",
                      padding: "2px 6px",
                      borderRadius: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      marginLeft: 8
                    }}>
                      {getStatusText(selectedQuotation.status)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                  Quotation Items ({selectedQuotation.items?.length || 0})
                </h3>
                {selectedQuotation.items?.map((item, index) => {
                  const imageUrl = getFirstImageUrl(item);
                  return (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      border: '1px solid #e9ecef',
                      borderRadius: '8px',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        background: '#eee',
                        flexShrink: 0
                      }}>
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={item.product_name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: '#bbb',
                            fontSize: '10px'
                          }}>
                            No Image
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                          {item.product_name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {formatPrice(item.unit_price)} × {item.quantity}
                        </div>
                      </div>
                      <div style={{ fontWeight: '600' }}>
                        {formatPrice(item.total_price)}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Total */}
              <div style={{
                background: '#f8f9fa',
                padding: '16px',
                borderRadius: '8px',
                textAlign: 'right'
              }}>
                <div style={{ fontSize: '18px', fontWeight: '600' }}>
                  Total: {formatPrice(selectedQuotation.total_amount)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default MyQuotationsPage;
